import {useEffect, useRef, useState} from 'react';
import {toast} from '@common-web/hooks/use-toast';
import {trpc} from '@common-web/lib/trpc';
import {triggerMemberExportDownload} from '@client/lib/memberExportDownload';

/**
 * Drives the sticky "export in progress" banner on Member Search / Group Members
 * and handles completion of a *backgrounded* export.
 *
 * The export dialog auto-downloads on completion while it's open. Once the user
 * clicks "Run in background" (or navigates away) the dialog stops polling, so
 * this hook takes over: it remembers the in-flight job id and keeps polling
 * `exportStatus` until the job reaches a terminal state, then fires the download
 * + toast (deduped via `triggerMemberExportDownload`) or surfaces a failure.
 *
 * Returns the `activeExport` query so the caller can render the banner exactly
 * as before.
 *
 * @param isDialogOpen whether the export dialog is currently open — while open
 *   the dialog owns completion handling, so this hook pauses its status poll to
 *   avoid duplicate work.
 */
export function useBackgroundMemberExport(isDialogOpen: boolean) {
  const activeExportQuery = trpc.vba.members.activeExport.useQuery(undefined, {
    refetchInterval: (query) => {
      if (!query.state.data) return false;
      return 3000;
    },
  });

  // Remember the most recent in-flight job so we can follow it to completion
  // even after `activeExport` flips back to null (which it does the moment the
  // job leaves PENDING/RUNNING).
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);
  const activeJobId = activeExportQuery.data?.jobRunId ?? null;
  useEffect(() => {
    if (activeJobId) setTrackedJobId(activeJobId);
  }, [activeJobId]);

  const statusQuery = trpc.vba.members.exportStatus.useQuery(
    {jobRunId: trackedJobId!},
    {
      // Only chase completion while the dialog is closed — otherwise the dialog
      // is already polling this same job and will handle the download itself.
      enabled: !!trackedJobId && !isDialogOpen,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (status === 'COMPLETED' || status === 'FAILED') return false;
        return 3000;
      },
    },
  );

  // Fire the terminal-state side effects once per tracked job.
  const handledJobRef = useRef<string | null>(null);
  const status = statusQuery.data?.status;
  useEffect(() => {
    if (!trackedJobId || isDialogOpen) return;
    if (handledJobRef.current === trackedJobId) return;

    if (status === 'COMPLETED') {
      handledJobRef.current = trackedJobId;
      const downloadUrl = statusQuery.data?.downloadUrl ?? null;
      if (downloadUrl) {
        triggerMemberExportDownload(trackedJobId, downloadUrl, statusQuery.data?.fileName ?? null);
      }
      setTrackedJobId(null);
    } else if (status === 'FAILED') {
      handledJobRef.current = trackedJobId;
      toast({
        title: 'Export failed',
        description: statusQuery.data?.errorMessage ?? 'The member export failed unexpectedly.',
        variant: 'destructive',
      });
      setTrackedJobId(null);
    }
  }, [status, trackedJobId, isDialogOpen, statusQuery.data]);

  // Bail out gracefully if status polling itself fails (network/auth/server) —
  // otherwise the hook keeps polling a job it can never resolve. Dedupe via the
  // same ref so we surface the error once per tracked job.
  const isError = statusQuery.isError;
  useEffect(() => {
    if (!trackedJobId || isDialogOpen || !isError) return;
    if (handledJobRef.current === trackedJobId) return;
    handledJobRef.current = trackedJobId;
    toast({
      title: 'Export status unavailable',
      description: 'We lost track of your export. Please try running it again.',
      variant: 'destructive',
    });
    setTrackedJobId(null);
  }, [isError, trackedJobId, isDialogOpen]);

  return activeExportQuery;
}
